/* eslint-disable max-lines-per-function */
import React, { useCallback, useEffect, useState } from 'react';
import TextField from '@mui/material/TextField';
import { Autocomplete, FormControl, Grid, InputAdornment } from '@mui/material';
import { useLang } from '~/hooks/useLang';
import useDispatchRegistrationObjectState from '../../../stores/DispatchRegistrationObjectState';
import useDispatchRegistrationState from '../../../stores/DispatchRegistrationState';
import { toast } from 'react-toastify';
import { IObjectDTO } from '../../../interfaces/IDispacthRegistrationObject';
import IObjectType from '~/features/ObjectType/interfaces/IObjectType';
import IUnitMeasurement from '~/features/UnitMeasurement/interfaces/IUnitMeasurement';
import ICircunstance from '~/features/Circunstance/interfaces/ICircunstance';
import IDestination from '~/features/Destination/interfaces/IDestination';
import useDispatchState from '~/features/Dispatch/stores/DispatchState';
import Buttons from '../../DispatchRegistrationTab/components/Buttons';
import notify from '~/utils/notify';
import FieldAutocompleteRegistration from '~/components/FieldAutocompleteRegistration';
import { Downgraded, useHookstate } from '@hookstate/core';
import FieldTextRegistration from '~/components/FieldTextRegistration';

interface IFormObject {
  readonly handleClickOpenConfirmAction: (action: string) => void
}

interface IErrosProps {
  readonly type: boolean;
  readonly circunstance: boolean;
  readonly destination: boolean;
  readonly unitMeasurement: boolean;
  readonly quantity: boolean;
}

export default function ObjectForm({ handleClickOpenConfirmAction }: IFormObject) {
  const loading = useHookstate<boolean>(false)
  const { dispatch } = useDispatchState();
  const { occurrence, status } = dispatch();
  const { id: occurrenceIdState, } = occurrence;
  const disableFields = status.value.id === 8

  const { registrationObjectDispatch, sendObject, editObject, getObjectTypes } = useDispatchRegistrationObjectState();

  const { registrationDispatch, getCircunstances, getDestinations, getUnitMeasurement } = useDispatchRegistrationState();
  const { addRegistration, inputErrors } = registrationDispatch();

  const { object } = registrationObjectDispatch();
  const { translate } = useLang();

  const [error, setError] = useState<IErrosProps>({
    type: false,
    circunstance: false,
    destination: false,
    unitMeasurement: false,
    quantity: false,
  });

  const someInvalidInput = () => {
    if (!object.get().objectType) {
      inputErrors.objectType.set(true)
    }
    if (!object.get().unitMeasurement) {
      inputErrors.unitMeasurement.set(true)
    }
    if (!object.get().circunstance) {
      inputErrors.circunstance.set(true)
    }
    if (!object.get().destination) {
      inputErrors.destination.set(true)
    }
    return Object.values(inputErrors.attach(Downgraded).get()).some(value => !!value)
  }

  const handleSend = useCallback(async () => {
    loading.set(true)
    if (someInvalidInput()) {
      return
    }
    await sendObject(object.get()).finally(() => {
      loading.set(false)
    })
    notify({ message: translate('Successfully created'), type: 'success' })
    addRegistration.set(false)
  }, [addRegistration, object, sendObject, translate])

  const handleEdit = useCallback(async () => {
    loading.set(true)
    await editObject(object.get()).finally(() => {
      loading.set(false)
    })
    notify({ message: translate('Saved successfully'), type: 'success' })
    addRegistration.set(false)
  }, [addRegistration, editObject, object, translate])


  useEffect(() => {
    object.set(prev => ({ ...prev, occurrenceId: occurrenceIdState.get() }))
  }, [])

  return (
    <FormControl fullWidth size='small' >
      <Grid container direction='row'>
        <Grid item xs={3} sx={{ padding: 1 }}>
          <FieldAutocompleteRegistration
            label={translate('Object Type')}
            onValueChange={(value) => object.objectType.set(value)}
            valueId={object.objectType.get()?.id as string}
            optionsFetch={getObjectTypes}
            isRequired
            disabled={disableFields}
            error={inputErrors.objectType.get()}
            onError={(value) => inputErrors.objectType.set(value)}
          />

        </Grid>
        <Grid item xs={3} sx={{ padding: 1 }}>
          <FieldAutocompleteRegistration
            valueId={object.unitMeasurement.get()?.id as string}
            onError={(value) => inputErrors.unitMeasurement.set(value)}
            error={inputErrors.unitMeasurement.get()}
            isRequired
            label={translate('Unit Measurement')}
            onValueChange={(value) => {
              object.set(prev => ({
                ...prev, unitMeasurement: {
                  id: value?.id ?? '',
                  name: value?.name ?? '',
                  symbol: value?.symbol ?? ''
                }
              }))
            }}
            optionsFetch={getUnitMeasurement}
            disabled={disableFields}
          />

        </Grid>
        <Grid item xs={1.5} sx={{ padding: 1 }}>
          <TextField
            disabled={disableFields}
            fullWidth
            size='small'
            id="outlined-quantity"
            error={!!error.quantity}
            helperText={error.quantity ?? ''}
            onFocus={() => setError({ ...error, quantity: false })}
            label={translate('Quantity')}
            value={object.quantity.get()}
            type="number"
            InputLabelProps={{
              shrink: true,
            }}
            InputProps={{
              inputProps: { min: 0, step: 0.01 },
              endAdornment: <InputAdornment position="end">
                {object.unitMeasurement?.get()?.symbol}
              </InputAdornment>,
              onChange: (event) =>
                object.quantity.set(parseFloat(event.target.value)),
            }}
          />
        </Grid>
        <Grid item xs={2.25} sx={{ padding: 1 }}>
          <FieldAutocompleteRegistration
            valueId={object.circunstance.get()?.id as string}
            isRequired
            onError={(value) => inputErrors.circunstance.set(value)}
            error={inputErrors.circunstance.get()}
            label={translate('Circunstance')}
            onValueChange={(circunstance) => {
              object.set(prev => ({ ...prev, circunstance }))
            }}
            optionsFetch={getCircunstances}
            disabled={disableFields}
          />

        </Grid>
        <Grid item xs={2.25} sx={{ padding: 1 }}>
          <FieldAutocompleteRegistration
            valueId={object.destination.get()?.id as string}
            isRequired
            onError={(value) => inputErrors.destination.set(value)}
            error={inputErrors.destination.get()}
            label={translate('Destination')}
            onValueChange={(destination) => {
              object.set(prev => ({ ...prev, destination }))
            }}
            optionsFetch={getDestinations}
            disabled={disableFields}
          />
        </Grid>
        <Grid item xs={12} sx={{ padding: 1, marginBottom: 1 }}>
          <FieldTextRegistration
            disableFields={disableFields}
            label={'Note'}
            value={object.notes.get()}
            onChangeValue={(value) => object.notes.set(value)}
            multiline={true}
            rows={4}
            height={92}
          />
        </Grid>
      </Grid>
      <Grid item xs={12}>
        <Buttons
          loading={loading}
          isUpdate={!!object.id.get()}
          handleClickOpenConfirmAction={handleClickOpenConfirmAction}
          handleEdit={handleEdit}
          handleSend={handleSend}
          someInvalidInput={someInvalidInput}
          disableFields={disableFields}
        />
      </Grid>
    </FormControl >
  );
}